/**
 * Imaging Worklist table: column identifiers, UI labels, and default visibility.
 * Kept separate from persistence helpers for clearer PR / reuse.
 */

export type ImagingColumnKey =
  | 'uhid'
  | 'patientName'
  | 'age'
  | 'modality'
  | 'studyDescription'
  | 'studyDateTime'
  | 'reportDateTime'
  | 'tests'
  | 'clinicalHistory'
  | 'appointmentType'
  | 'priority'
  | 'reportStatus'
  | 'aiResult'
  | 'assigned'
  | 'assignedHubFacility'
  | 'sourceFacility'
  | 'action';

export type HubSpokeWorklistColumnKey = 'assignedHubFacility' | 'sourceFacility';

export const IMAGING_COLUMN_LABELS: Record<ImagingColumnKey, string> = {
  uhid: 'UHID',
  patientName: 'Patient Name',
  age: 'Age & Gender',
  modality: 'Modality',
  studyDescription: 'Study Description',
  studyDateTime: 'Study Date & Time',
  reportDateTime: 'Report Date & Time',
  tests: 'Tests',
  clinicalHistory: 'Clinical History',
  appointmentType: 'Appointment Type',
  priority: 'Priority',
  reportStatus: 'Report Status',
  aiResult: 'AI Result',
  assigned: 'Assigned',
  assignedHubFacility: 'Assigned Hub',
  sourceFacility: 'Source Facility',
  action: 'Action',
};

export function normalizeWorklistFacilityType(facilityType?: string | null): string {
  return String(facilityType || '')
    .trim()
    .toUpperCase();
}

/** SPOKE shows Assigned Hub; HUB shows Source Facility; other facility types hide both. */
export function getHubSpokeWorklistColumnKey(
  facilityType?: string | null
): HubSpokeWorklistColumnKey | null {
  const normalized = normalizeWorklistFacilityType(facilityType);
  if (normalized === 'SPOKE') {
    return 'assignedHubFacility';
  }
  if (normalized === 'HUB') {
    return 'sourceFacility';
  }
  return null;
}

/** Default on/off per column before layout-specific overrides (e.g. Assigned). */
export const IMAGING_COLUMNS_DEFAULT_VISIBILITY: Record<ImagingColumnKey, boolean> = {
  uhid: true,
  patientName: true,
  age: true,
  modality: true,
  studyDescription: true,
  studyDateTime: true,
  reportDateTime: true,
  tests: true,
  clinicalHistory: true,
  appointmentType: true,
  priority: true,
  reportStatus: true,
  aiResult: true,
  assigned: true,
  assignedHubFacility: true,
  sourceFacility: true,
  action: true,
};
